import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '../services/query/queryKeys';

interface UseLeadPollingOptions {
  campaignId: string;
  leadId: string;
  userEmail?: string;
  enabled: boolean;
  onStatusChange?: (newStatus: string) => void;
  shouldStopPolling?: (newStatus: string) => boolean;
  onTimeout?: () => void;
  pollingInterval?: number;
  maxPollingDuration?: number;
}

/**
 * Background polling hook for lead status changes.
 *
 * Only refetches the single lead detail query and fires the callback.
 * Does NOT write to grid caches during polling — that caused cascading
 * re-renders that froze the browser. Grid cache is updated only on
 * completion via invalidateQueries.
 *
 * Lifecycle note (verified): refetchQueries defaults to type:'active', which
 * no-ops once the detail query goes inactive. This cannot strand the poll:
 * the sole host of this hook is the LeadReportModal Sidebar, mounted
 * together with the useLeadDetail observer — while polling runs the query is
 * guaranteed active, and closing the modal unmounts the poll hook in the
 * same tick. Do not mount this hook anywhere its detail query can go
 * inactive while polling is enabled.
 */
export const useLeadPolling = ({
  campaignId,
  leadId,
  userEmail,
  enabled,
  onStatusChange,
  shouldStopPolling,
  onTimeout,
  pollingInterval = 3000,
  maxPollingDuration = 120000,
}: UseLeadPollingOptions) => {
  const queryClient = useQueryClient();
  const startTimeRef = useRef<number>(0);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const previousStatusRef = useRef<string | null>(null);
  const isPollingRef = useRef(false);

  // Store callbacks in refs so the effect never restarts due to inline function identity changes
  const onStatusChangeRef = useRef(onStatusChange);
  onStatusChangeRef.current = onStatusChange;
  const shouldStopPollingRef = useRef(shouldStopPolling);
  shouldStopPollingRef.current = shouldStopPolling;
  const onTimeoutRef = useRef(onTimeout);
  onTimeoutRef.current = onTimeout;

  useEffect(() => {
    const clearPollingTimeout = () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };

    const stopPolling = () => {
      clearPollingTimeout();
      isPollingRef.current = false;
    };

    if (!enabled) {
      stopPolling();
      previousStatusRef.current = null;
      return;
    }

    startTimeRef.current = Date.now();
    previousStatusRef.current =
      ((queryClient.getQueryData(
        queryKeys.leads.detail(campaignId, leadId)
      ) as { status?: string } | undefined)?.status ?? null);

    const poll = async () => {
      if (isPollingRef.current) return;

      isPollingRef.current = true;
      const elapsed = Date.now() - startTimeRef.current;

      if (elapsed > maxPollingDuration) {
        stopPolling();
        onTimeoutRef.current?.();
        return;
      }

      try {
        await queryClient.refetchQueries({
          queryKey: queryKeys.leads.detail(campaignId, leadId),
        });

        const freshData = queryClient.getQueryData(
          queryKeys.leads.detail(campaignId, leadId)
        ) as any;

        if (freshData?.status && freshData.status !== previousStatusRef.current) {
          previousStatusRef.current = freshData.status;

          onStatusChangeRef.current?.(freshData.status);

          const shouldStop = shouldStopPollingRef.current
            ? shouldStopPollingRef.current(freshData.status)
            : true;

          if (shouldStop) {
            stopPolling();

            // Now that polling is done, sync grid caches via invalidation
            // so the table picks up the final status
            queryClient.invalidateQueries({
              queryKey: queryKeys.leads.byCampaign(campaignId),
            });
            if (userEmail) {
              queryClient.invalidateQueries({
                queryKey: queryKeys.leads.byCampaign(campaignId, userEmail),
              });
            }
            return;
          }
        }
      } finally {
        isPollingRef.current = false;
      }

      if (enabled) {
        timeoutRef.current = setTimeout(poll, pollingInterval);
      }
    };

    poll();

    return () => {
      stopPolling();
      previousStatusRef.current = null;
    };
  }, [enabled, campaignId, leadId, userEmail, pollingInterval, maxPollingDuration, queryClient]);

  return {
    stopPolling: () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      isPollingRef.current = false;
    },
  };
};
